import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { enhancedApi } from '../api';

const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRole, setMenuRole] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.getOrganizationalRoles();
      const data = response.data;
      setRoles(Array.isArray(data) ? data : (data?.data || []));
      setError('');
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, role) => {
    setAnchorEl(event.currentTarget);
    setMenuRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRole(null);
  };

  const handleOpenCreate = () => {
    setRoleName('');
    setEditingRole(null);
    setOpenCreateDialog(true);
  };

  const handleCloseCreate = () => {
    setOpenCreateDialog(false);
    setRoleName('');
  };

  const handleOpenEdit = (role) => {
    setEditingRole(role);
    setRoleName(role.name || '');
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleCloseEdit = () => {
    setOpenEditDialog(false);
    setEditingRole(null);
    setRoleName('');
  };

  const handleSaveCreate = async () => {
    const name = (roleName || '').trim();
    if (!name) {
      setError('Role name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await enhancedApi.createOrganizationalRole({ name });
      fetchRoles();
      handleCloseCreate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const name = (roleName || '').trim();
    if (!name || !editingRole) {
      setError('Role name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await enhancedApi.updateOrganizationalRole(editingRole.id, { name });
      fetchRoles();
      handleCloseEdit();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? This will remove the role from client assignments.`)) {
      handleMenuClose();
      return;
    }
    try {
      await enhancedApi.deleteOrganizationalRole(role.id);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
    handleMenuClose();
  };

  const formatDateCreated = (role) => {
    if (!role.created_at) return '—';
    const date = new Date(role.created_at);
    const who = role.created_by_name || 'Unknown';
    return `${who} · ${date.toLocaleDateString()}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ fontWeight: 600 }}
        >
          Create role
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography>Loading roles...</Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#fafbfc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Date Created</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Applied To</TableCell>
              <TableCell sx={{ width: 48 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length > 0 ? (
              roles.map((role) => (
                <TableRow key={role.id} sx={{ borderBottom: '1px solid #f0f0f0' }}>
                  <TableCell sx={{ fontWeight: 500 }}>{role.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 14 }}>
                    {formatDateCreated(role)}
                  </TableCell>
                  <TableCell>{role.applied_to_count ?? 0}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, role)}
                      aria-label="More actions"
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && menuRole?.id === role.id}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => handleOpenEdit(menuRole)}>Edit</MenuItem>
                      <MenuItem onClick={() => handleDelete(menuRole)} sx={{ color: 'error.main' }}>
                        Delete
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No roles yet. Click &quot;Create role&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Create Role Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role name"
            fullWidth
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="e.g., Bookkeeper, Tax Preparer"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate}>Cancel</Button>
          <Button onClick={handleSaveCreate} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role name"
            fullWidth
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesTab;
